/* global axios */
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const POLL_INTERVAL_MS = 30 * 1000;

// Module-level singletons — shared across all callers so only one interval runs.
const dismissedInboxIds = ref(new Set());
let timerId = null;
let mountCount = 0;

export function useWhatsappQrStatusPolling() {
  const store = useStore();
  const { accountId } = useAccount();
  const inboxes = useMapGetter('inboxes/getInboxes');

  const qrInboxes = computed(() =>
    inboxes.value.filter(i => i.channel_type === INBOX_TYPES.WHATSAPP_QR)
  );

  const disconnectedInbox = computed(() => {
    return (
      qrInboxes.value.find(
        i => i.connection_status && i.connection_status !== 'connected'
      ) || null
    );
  });

  const fetchStatusForInbox = async inbox => {
    try {
      const { data } = await axios.get(
        `/api/v1/accounts/${accountId.value}/inboxes/${inbox.id}/uazapi/status`
      );
      if (!data || !data.status) return;
      if (data.status === inbox.connection_status) return;
      store.dispatch('inboxes/updateInboxConnectionStatus', {
        id: inbox.id,
        connection_status: data.status,
      });
    } catch {
      // silencioso — falha de polling não deve quebrar UI
    }
  };

  const pollAll = () => {
    qrInboxes.value.forEach(fetchStatusForInbox);
  };

  const start = () => {
    if (timerId) return;
    pollAll();
    timerId = setInterval(pollAll, POLL_INTERVAL_MS);
  };

  const stop = () => {
    if (timerId) {
      clearInterval(timerId);
      timerId = null;
    }
  };

  const dismissInbox = inboxId => {
    const next = new Set(dismissedInboxIds.value);
    next.add(inboxId);
    dismissedInboxIds.value = next;
  };

  const isDismissed = inboxId => dismissedInboxIds.value.has(inboxId);

  // When an inbox comes back to connected, remove from dismiss set so
  // a future disconnection will reopen the modal.
  watch(disconnectedInbox, (current, previous) => {
    if (previous && !current) {
      const next = new Set(dismissedInboxIds.value);
      next.delete(previous.id);
      dismissedInboxIds.value = next;
    }
  });

  mountCount += 1;
  onBeforeUnmount(() => {
    mountCount -= 1;
    if (mountCount === 0) stop();
  });

  // Start polling when at least one QR inbox exists.
  watch(
    qrInboxes,
    list => {
      if (list.length > 0) start();
      else stop();
    },
    { immediate: true }
  );

  return {
    disconnectedInbox,
    dismissInbox,
    isDismissed,
    start,
    stop,
  };
}
